import {
  answersSaved,
  useOptionalSurvey,
  useSaveQuestionAnswers
} from '~/training/sequence/SequenceAnswers';
import React, { DragEventHandler, useEffect, useState } from 'react';
import { x } from '@xstyled/styled-components';
import {
  AudioDefinition,
  DRAG_ZONE
} from '~/training/sequence/templates/items';
import { useTrainingStore } from '~/training/state';
import { Capsule } from '~/ui/capsules';
import { Card } from '~/ui/card/Card';
import { Flex } from '~/ui/flex/Flex';
import { Item, registry, RenderProps } from '../Item';
import { useDragAndDropStore } from './DragAndDropStore';
import { t } from 'ttag';
import { AddCards } from './AddCards';
import { useEditorStore } from '~/shell/Store';
import { shouldSendInteractions, useLmsAdapter } from '~/lms';
interface Props {
  items?: Array<{
    correct: boolean;
    feedback: string;
    feedbackAudios?: AudioDefinition;
    id: string;
    value: string;
  }>;
  question?: string;
  llamaSurvey?: boolean | null;
}

const DragAndDropItem = (props: {
  id: string;
  selected?: boolean;
  label?: string;
  value?: string;
  correct?: boolean;
  onClickItem?: (id: string) => void; // repurposed for selection toggle
  onDragStateChange?: (dragging: boolean) => void;
  isDragging?: boolean;
  disabled?: boolean;
  editorMode?: boolean;
  contentEditable?: boolean;
}) => {
  const { dropped, setDropped, setLastId } = useDragAndDropStore();

  const onDragStart: DragEventHandler<HTMLDivElement> = event => {
    event.dataTransfer.setData('text/plain', props.id);
    event.dataTransfer.dropEffect = 'move';
    event.currentTarget.style.opacity = '0.9';
    props.onDragStateChange?.(true);
  };

  const droppedEvent = () => {
    const set = new Set([...dropped, props.id]);
    setDropped([...set]);
    setLastId(props.id);
  };

  const onDragEnd: DragEventHandler<HTMLDivElement> = event => {
    const elemBelow = document.elementFromPoint(event.clientX, event.clientY);

    if (!elemBelow) return;

    const droppableBelow = elemBelow.closest('.drop_zone_box');

    if (elemBelow?.getAttribute('draggable') && droppableBelow) {
      droppedEvent();
    }

    const dropItem = event.target as HTMLDivElement;

    if (dropItem?.parentElement?.id == 'drag_zone') {
      const set = new Set([...dropped, props.id]);
      setDropped([...set]);
      setLastId(props.id);
    }
    props.onDragStateChange?.(false);
  };

  const onDrop: DragEventHandler<HTMLDivElement> = event => {
    event.preventDefault();
    event.stopPropagation();
  };

  let borderColor = 'white';
  if (props.editorMode && !props.contentEditable) {
    borderColor = props.correct ? 'green' : 'red';
  } else if (props.selected) {
    borderColor = 'purple';
  }

  return (
    <Card
      draggable={!props.disabled}
      onDragStart={props.disabled ? undefined : onDragStart}
      onDragEnd={props.disabled ? undefined : onDragEnd}
      onDrop={props.disabled ? undefined : onDrop}
      onClick={e => {
        if (props.disabled) return;
        if (props.onClickItem) {
          e.stopPropagation();
          props.onClickItem(props.id);
          droppedEvent();
        }
      }}
      onKeyDown={e => {
        if (props.disabled) return;
        if ((e.key === 'Enter' || e.key === ' ') && props.onClickItem) {
          e.preventDefault();
          props.onClickItem(props.id);
          droppedEvent();
        }
      }}
      tabIndex={props.disabled ? -1 : props.onClickItem ? 0 : -1}
      role={props.onClickItem ? 'button' : undefined}
      aria-pressed={props.onClickItem ? !!props.selected : undefined}
      {...(props.disabled ? { 'aria-disabled': true } : {})}
      p="sm lg"
      borderStyle="solid"
      borderWidth="1px"
      borderColor={borderColor}
      cursor={
        props.disabled ? 'default' : props.onClickItem ? 'pointer' : 'grab'
      }
      className="DragAndDropItem"
      position="relative"
      data-item-id={props.id}
    >
      <Capsule
        aria-hidden={true}
        fontWeight="bold"
        float="left"
        marginRight={'10px'}
      >
        {props?.label}
      </Capsule>
      <p
        style={{ float: 'left', display: 'contents' }}
        dangerouslySetInnerHTML={{ __html: props?.value || '' }}
      />
    </Card>
  );
};

export class DragZone extends Item<typeof DRAG_ZONE, Props> {
  protected render({
    id,
    children,
    items,
    question,
    llamaSurvey
  }: RenderProps<Props>) {
    const [openAddCards, setOpenAddCards] = useState(false);
    const { editorMode, contentEditable, isReadOnlyView } = useEditorStore();
    // Once the learner completed a pretest in this attempt, surveys are optional, as the server treats them.
    const optionalSurvey = useOptionalSurvey(llamaSurvey === true);
    const savesAnswers = useTrainingStore(s => answersSaved(s, optionalSurvey));
    const saveQuestionAnswers = useSaveQuestionAnswers();
    const { dropped, setDropped, lastId } = useDragAndDropStore();
    const lms = useLmsAdapter();

    const correctItems = items?.filter(i => i.correct) || [];

    items = items?.map((item, index) => {
      return { ...item, label: String.fromCharCode(97 + index) };
    });

    const onDragOver: DragEventHandler<HTMLDivElement> = e => {
      e.stopPropagation();
      e.preventDefault();
    };

    const onDrop: DragEventHandler<HTMLDivElement> = e => {
      e.stopPropagation();
      e.preventDefault();
      const id = e.dataTransfer.getData('text/plain');
      setDropped(dropped.filter(i => i !== id));
    };

    useEffect(() => {
      if (dropped.length >= correctItems.length && dropped.length > 0) {
        const correctAnswer = correctItems.map(i => i.id);
        const isCorrect = correctAnswer.every(element =>
          dropped.includes(element)
        );

        if (shouldSendInteractions() && lms.isAvailable) {
          lms.recordMultipleChoiceInteraction(
            id,
            dropped,
            isCorrect,
            correctAnswer,
            question
          );
        }
      }
    }, [dropped, lastId, items]);

    useEffect(() => {
      if (lastId) {
        if (savesAnswers) {
          saveQuestionAnswers(lastId);
        }
      }
    }, [lastId]);

    const [dragging, setDragging] = useState(false);
    // Click selection toggle (learner mode)
    const toggleSelect = (itemId: string) => {
      const current = [...dropped];
      const exists = current.includes(itemId);
      const updated = exists
        ? current.filter((id: string) => id !== itemId)
        : [...current, itemId];
      setDropped(updated);
    };

    return (
      <Flex w="100%" h="100%" alignItems="center">
        <Flex
          w="100%"
          flexDirection={'column'}
          justifyContent="center"
          px="24px" /* add horizontal padding for text + gray edit section */
          pt="24px" /* top padding to match horizontal spacing */
          pb="24px" /* bottom padding for symmetry */
          boxSizing="border-box"
        >
          {children}
          {contentEditable ? (
            <>
              <AddCards
                open={openAddCards}
                onClose={() => setOpenAddCards(false)}
                items={items?.map(i => ({
                  id: i.id,
                  value: i.value,
                  correct: i.correct
                }))}
              />
              {/** Determine button label based on whether any cards exist */}
              {(() => {
                const hasCards = (items?.length || 0) > 0;
                const ctaLabel = hasCards ? t`Edit cards` : t`Add Cards`;
                return (
                  <Flex
                    bg="greyLight"
                    borderRadius="lg"
                    w="100%"
                    minWidth="340px"
                    minHeight="360px" /* a bit taller for squarer feel */
                    marginTop="20px"
                    mx="auto"
                    p="50px"
                    alignItems="center"
                    justifyContent="center"
                    pointerEvents="all"
                    boxSizing="border-box"
                  >
                    <Flex
                      id="drag_zone"
                      w="100%"
                      h="240px" /* taller so white area is closer to square */
                      borderRadius="20px"
                      bg="white"
                      position="relative"
                      overflow="hidden"
                      alignItems="center"
                      justifyContent="center"
                      onDragOver={onDragOver}
                      onDrop={onDrop}
                      role="region"
                      aria-label={ctaLabel}
                      tabIndex={0}
                      style={{ cursor: 'pointer' }}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setOpenAddCards(true);
                        }
                      }}
                    >
                      <x.button
                        aria-label={ctaLabel}
                        onClick={() => setOpenAddCards(true)}
                        onMouseDown={e => e.preventDefault()}
                        tabIndex={-1}
                        style={{
                          position: 'absolute',
                          inset: 0,
                          background: 'transparent',
                          border: 'none',
                          padding: 0,
                          margin: 0,
                          cursor: 'pointer',
                          zIndex: 5,
                          outline: 'none',
                          boxShadow: 'none',
                          userSelect: 'none'
                        }}
                      />
                      <x.button
                        w="157px"
                        h="38px"
                        backgroundColor="#9738FF"
                        color="white"
                        fontSize="16px"
                        fontWeight={600}
                        border="none"
                        borderRadius="8px"
                        cursor="pointer"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        onClick={() => setOpenAddCards(true)}
                        aria-label={ctaLabel}
                      >
                        {ctaLabel}
                      </x.button>
                      <svg
                        width="100%"
                        height="100%"
                        viewBox="0 0 300 240" /* updated height to match new white area */
                        preserveAspectRatio="none"
                        style={{
                          position: 'absolute',
                          inset: 0,
                          pointerEvents: 'none',
                          zIndex: 4
                        }}
                        aria-hidden="true"
                      >
                        <rect
                          x="1"
                          y="1"
                          width="298"
                          height="238"
                          fill="none"
                          stroke="#9738FF"
                          strokeWidth="1" /* thinner line */
                          strokeDasharray="4 4" /* more frequent dashes */
                          rx="20"
                          ry="20"
                        />
                      </svg>
                    </Flex>
                  </Flex>
                );
              })()}
            </>
          ) : (
            <Flex
              id="drag_zone"
              aria-label={t`List of items to select`}
              onDragOver={onDragOver}
              onDrop={onDrop}
              variant="list"
              bg={{ xs: 'transparent', lg: 'greyLight' }}
              borderRadius="lg"
              p={{ _: '0', md: 'md', lg: 'xxl' }}
              alignItems="center"
              justifyContent="space-around"
              pointerEvents="all"
              gap="sm"
              marginTop="20px"
              w="95%"
              flexDirection={{ xs: 'row', lg: 'column', _: 'column' }}
              role={'listbox'}
            >
              {items
                ?.filter(i => !dropped.includes(i.id))
                .map(i => (
                  <DragAndDropItem
                    key={i.id}
                    {...i}
                    selected={false}
                    onClickItem={toggleSelect}
                    onDragStateChange={setDragging}
                    isDragging={dragging}
                    disabled={isReadOnlyView}
                    editorMode={editorMode}
                    contentEditable={contentEditable}
                  />
                ))}
            </Flex>
          )}
        </Flex>
      </Flex>
    );
  }
}

registry[DRAG_ZONE] = DragZone;
